import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Plus, Trash2 } from "lucide-react";

/**
 * CategoryModal — Diálogos de creación y edición de categorías del catálogo.
 *
 * Extraído 1:1 desde `pages/CatalogPage.js`. Agrupa los dos diálogos de categoría:
 *  - "Nueva Categoría": nombre, bloques del Package Builder y estado activo.
 *  - "Editar Categoría": lo anterior + editor de campos técnicos (plantilla de
 *    información técnica catalog-driven que consume CustomerDetail).
 *
 * Eventos: al confirmar dispara `handleCreateCategory` / `handleUpdateCategory`;
 * el cierre limpia el estado vía `setCategoryModalOpen(false)` / `setEditingCategory(null)`.
 *
 * @param {boolean}     props.categoryModalOpen    Apertura del diálogo "Nueva Categoría".
 * @param {Function}    props.setCategoryModalOpen  Setter del flag de apertura.
 * @param {Object}      props.newCategory           Estado del formulario de categoría nueva.
 * @param {Function}    props.setNewCategory        Setter del formulario de categoría nueva.
 * @param {Object|null} props.editingCategory       Categoría en edición (null = cerrado).
 * @param {Function}    props.setEditingCategory    Setter de la categoría en edición.
 * @param {Array}       props.BLOQUE_OPTIONS        Opciones de bloques del Package Builder.
 * @param {Function}    props.handleCreateCategory  Handler de creación de categoría.
 * @param {Function}    props.handleUpdateCategory  Handler de actualización de categoría.
 */
export const CategoryModal = ({
  categoryModalOpen,
  setCategoryModalOpen,
  newCategory,
  setNewCategory,
  editingCategory,
  setEditingCategory,
  BLOQUE_OPTIONS,
  handleCreateCategory,
  handleUpdateCategory,
}) => {
  return (
    <>
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Nueva Categoría</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Nombre de la categoría *</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="Ej: Seguridad Informática"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Bloques en Armar Paquete</Label>
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto border border-[#1E1E5F] rounded-md p-2 bg-[#0A0A35]" data-testid="category-bloques-checkboxes">
                {BLOQUE_OPTIONS.filter(opt => opt.value).map((opt) => {
                  const checked = (newCategory.bloques_package_builder || []).includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 py-1 px-1 text-sm text-slate-200 hover:bg-[#1B1B4B]/40 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const current = newCategory.bloques_package_builder || [];
                          const next = e.target.checked
                            ? [...current, opt.value]
                            : current.filter(v => v !== opt.value);
                          setNewCategory({ ...newCategory, bloques_package_builder: next });
                        }}
                        className="h-4 w-4 rounded border-[#1E1E5F] accent-[#42E695]"
                        data-testid={`category-bloque-${opt.value}`}
                      />
                      <span>{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-1">Una categoría puede aparecer en uno o varios bloques del constructor de paquetes.</p>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Activa</Label>
              <Switch
                checked={newCategory.is_active}
                onCheckedChange={(v) => setNewCategory({ ...newCategory, is_active: v })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setCategoryModalOpen(false)}
                className="border-[#1E1E5F] text-slate-300"
              >
                Cancelar
              </Button>
              <Button onClick={handleCreateCategory} className="bg-[#42E695] text-[#04042C]">
                Crear Categoría
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Editar Categoría</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Nombre de la categoría *</Label>
                <Input
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Bloques en Armar Paquete</Label>
                <div className="mt-2 space-y-1 max-h-60 overflow-y-auto border border-[#1E1E5F] rounded-md p-2 bg-[#0A0A35]" data-testid="edit-category-bloques-checkboxes">
                  {BLOQUE_OPTIONS.filter(opt => opt.value).map((opt) => {
                    const checked = (editingCategory.bloques_package_builder || []).includes(opt.value);
                    return (
                      <label key={opt.value} className="flex items-center gap-2 py-1 px-1 text-sm text-slate-200 hover:bg-[#1B1B4B]/40 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = editingCategory.bloques_package_builder || [];
                            const next = e.target.checked
                              ? [...current, opt.value]
                              : current.filter(v => v !== opt.value);
                            setEditingCategory({ ...editingCategory, bloques_package_builder: next });
                          }}
                          className="h-4 w-4 rounded border-[#1E1E5F] accent-[#42E695]"
                          data-testid={`edit-category-bloque-${opt.value}`}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-1">Una categoría puede aparecer en uno o varios bloques del constructor de paquetes.</p>
              </div>

              {/* Campos técnicos — Catalog-driven template for CustomerDetail info técnica */}
              <div className="border-t border-[#1E1E5F] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-slate-300">Campos técnicos</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Plantilla de información técnica para los servicios de esta categoría. Si queda vacía, CustomerDetail usa la plantilla por defecto.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const rows = Array.isArray(editingCategory.campos_tecnicos) ? editingCategory.campos_tecnicos : [];
                      setEditingCategory({
                        ...editingCategory,
                        campos_tecnicos: [...rows, { key: "", label: "", type: "text", options: [], sensitive: false, required: false }],
                      });
                    }}
                    className="border-[#1E1E5F] text-[#03BEE4] text-xs h-7"
                    data-testid="add-campo-tecnico-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Añadir campo
                  </Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-[#1E1E5F] rounded-md p-2 bg-[#0A0A35]">
                  {(!editingCategory.campos_tecnicos || editingCategory.campos_tecnicos.length === 0) ? (
                    <p className="text-xs text-slate-500 text-center py-2">Sin campos técnicos configurados — se usará la plantilla por defecto.</p>
                  ) : (
                    editingCategory.campos_tecnicos.map((field, i) => {
                      const updateField = (patch) => {
                        const rows = [...editingCategory.campos_tecnicos];
                        rows[i] = { ...rows[i], ...patch };
                        setEditingCategory({ ...editingCategory, campos_tecnicos: rows });
                      };
                      const removeField = () => {
                        const rows = editingCategory.campos_tecnicos.filter((_, j) => j !== i);
                        setEditingCategory({ ...editingCategory, campos_tecnicos: rows });
                      };
                      return (
                        <div key={i} className="border border-[#1E1E5F] rounded p-2 space-y-1 bg-[#0F0F3D]">
                          <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                            <Input
                              value={field.key || ""}
                              onChange={(e) => updateField({ key: e.target.value })}
                              placeholder="key (ej. numero_linea)"
                              className="h-7 bg-[#0A0A35] border-[#1E1E5F] text-white text-xs"
                              data-testid={`campo-tecnico-key-${i}`}
                            />
                            <Input
                              value={field.label || ""}
                              onChange={(e) => updateField({ label: e.target.value })}
                              placeholder="label (ej. Número de línea)"
                              className="h-7 bg-[#0A0A35] border-[#1E1E5F] text-white text-xs"
                              data-testid={`campo-tecnico-label-${i}`}
                            />
                            <Select
                              value={field.type || "text"}
                              onValueChange={(v) => updateField({ type: v })}
                            >
                              <SelectTrigger className="h-7 w-28 bg-[#0A0A35] border-[#1E1E5F] text-white text-xs" data-testid={`campo-tecnico-type-${i}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                                {["text", "password", "textarea", "boolean", "date", "select"].map(t => (
                                  <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              type="button"
                              onClick={removeField}
                              className="text-red-400 hover:text-red-300 p-1"
                              data-testid={`campo-tecnico-remove-${i}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {field.type === "select" && (
                            <Input
                              value={Array.isArray(field.options) ? field.options.join(", ") : ""}
                              onChange={(e) => updateField({ options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                              placeholder="opciones separadas por coma (ej. principal, DDI, portado)"
                              className="h-7 bg-[#0A0A35] border-[#1E1E5F] text-white text-xs"
                              data-testid={`campo-tecnico-options-${i}`}
                            />
                          )}
                          <div className="flex items-center gap-4 text-xs text-slate-400">
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!field.sensitive}
                                onChange={(e) => updateField({ sensitive: e.target.checked })}
                                className="h-3 w-3 accent-[#03BEE4]"
                                data-testid={`campo-tecnico-sensitive-${i}`}
                              />
                              Sensible (cifrado / enmascarado)
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!field.required}
                                onChange={(e) => updateField({ required: e.target.checked })}
                                className="h-3 w-3 accent-[#03BEE4]"
                                data-testid={`campo-tecnico-required-${i}`}
                              />
                              Requerido
                            </label>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Activa</Label>
                <Switch
                  checked={editingCategory.is_active !== false}
                  onCheckedChange={(v) => setEditingCategory({ ...editingCategory, is_active: v })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditingCategory(null)}
                  className="border-[#1E1E5F] text-slate-300"
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdateCategory} className="bg-[#42E695] text-[#04042C]">
                  Guardar Cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
